import { useContext, useEffect, useState } from 'react';

import config from '@config';
import { Logger } from '@utils/logger';
import { msToTime } from '@utils/time';
import { useInterval } from '@hooks/use-interval';

import { AppContext } from '@/App.context';

import {
	IS_AUTHENTICATED_KEY,
	REFRESH_TOKEN_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	IS_SUBSCRIBED_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	APP_USER_ID,
} from '../constants';
import { DateTime, DurationLike } from 'luxon';

const logger = new Logger('Token', {
	log: 'rgb(88, 86, 214)',
	debug: 'rgb(54, 52, 163)',
	info: 'rgb(125, 122, 255)',
	error: 'rgb(211, 15, 69)',
});

export function useAuthEffect({
	isSupported,
}: AuthEffectArgs): AuthEffectResponse {
	const { onlineStatus } = useContext(AppContext);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const [accessTokenTrigger, setAccessTokenTrigger] = useState<
		undefined | number
	>();
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
		isSupported
			? localStorage.getItem(IS_AUTHENTICATED_KEY) !== null
			: false,
	);
	const [refreshToken, setRefreshToken] = useState<string | undefined>();
	const [refreshExpirationDate, setRefreshExpirationDate] = useState<
		DateTime | undefined
	>();
	const { accessToken, accessExpirationDate } = useFetchAccessToken(
		refreshToken,
		accessTokenTrigger,
	);

	const redirectUrl = '/';

	useEffect(() => {
		// If the user is not authenticated don't do anything
		if (!isAuthenticated) {
			return;
		}

		// If you are offline don't try to do anything
		if (!onlineStatus) {
			return;
		}

		// This logic is only applicable to refresh token on load
		if (refreshToken) {
			return;
		}

		const token = localStorage.getItem(REFRESH_TOKEN_KEY);
		if (!token) {
			clearTokens(redirectUrl);
			setIsAuthenticated(false);
			return;
		}

		const refreshTokenData = parseJwt(token);
		setRefreshExpirationDate(DateTime.fromSeconds(refreshTokenData.exp));
		setRefreshToken(token);
	}, [onlineStatus, refreshToken]);

	// In this effect we check the expiration date of the refresh token
	useEffect(() => {
		const now = DateTime.now();
		if (!refreshExpirationDate) {
			return;
		}

		// If now is larger than refresh expiration date clear the tokens
		if (now >= refreshExpirationDate) {
			clearTokens(redirectUrl);
			setIsAuthenticated(false);
			return;
		}
	}, [refreshExpirationDate]);

	return {
		isAdmin,
		isAuthenticated,
		accessToken,
		accessExpirationDate,
		refreshToken,
		refreshExpirationDate,
		triggerRefreshToken: () => {
			setAccessTokenTrigger(DateTime.now().toSeconds());
		},
	};
}

interface AuthEffectArgs {
	isSupported: boolean;
}

interface AuthEffectResponse {
	isAuthenticated: boolean;
	accessToken?: string;
	accessExpirationDate?: DateTime;
	refreshToken?: string;
	refreshExpirationDate?: DateTime;
	isAdmin: boolean;
	triggerRefreshToken: () => void;
}

function clearTokens(redirect?: string) {
	const keys = [
		IS_AUTHENTICATED_KEY,
		USER_ROLE,
		IS_SUBSCRIBED_KEY,
		ACCESS_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		REFRESH_TOKEN_KEY,
		ACCESS_TOKEN_EXPIRE_AT_KEY,
		REFRESH_TOKEN_EXPIRATION_KEY,
		APP_USER_ID,
	];
	for (const k of keys) {
		if (localStorage.getItem(k)) {
			localStorage.removeItem(k);
		}
	}

	if (redirect) {
		location.href = redirect;
	}
}

function useFetchAccessToken(
	refreshToken?: string,
	trigger?: number,
): AccessTokenResponse {
	const { graphQLClient } = useContext(AppContext);
	const [accessToken, setAccessToken] = useState<string | undefined>();
	const [accessExpirationDate, setAccessExpirationDate] = useState<
		DateTime | undefined
	>();
	useEffect(() => {
		if (!refreshToken) return;
		refreshAccessToken(refreshToken)
			.then(r => {
				graphQLClient.setHeader(
					'Authorization',
					`Bearer ${r.accessToken}`,
				);

				setAccessToken(r.accessToken);
				setAccessExpirationDate(r.accessExpirationDate);
			})
			.catch(console.error);
	}, [refreshToken, trigger]);

	return {
		accessToken,
		accessExpirationDate,
	};
}

export async function refreshAccessToken(
	token: string,
): Promise<AccessTokenResponse> {
	const queryParams = new URLSearchParams({
		refresh_token: token,
	}).toString();
	const url = `${config.build.host}/auth/token?${queryParams}`;
	const rawResponse = await fetch(url, {
		method: 'GET',
		headers: {
			'app-key': config.build.appKey,
			'Content-Type': 'application/json',
		},
	});
	const content = await rawResponse.json();
	const { expires } = content;

	// Calculate expiration dates
	const accessToken = content.access_token;
	const accessExpirationDate = DateTime.now().plus({ seconds: expires });

	return {
		accessToken,
		accessExpirationDate,
	};
}

interface AccessTokenResponse {
	accessToken?: string;
	accessExpirationDate?: DateTime;
}

export function useTrackAccessTokens(args: TrackRefreshTokenArgs) {
	const {
		isSupported,
		interval,
		isAuthenticated,
		isAdmin,
		accessToken,
		accessExpirationDate,
		refreshToken,
		refreshExpirationDate,
		durationOffset,
		triggerRefreshToken,
	} = args;
	const redirect = '/';
	const { onlineStatus } = useContext(AppContext);
	useInterval(
		() => {
			// If the feature is not supported don't do anything
			if (!isSupported) return;
			// If you are not authenticated you don't do anything
			if (!isAuthenticated) return;
			// If you are an admin i don't need to refresh your token
			if (isAdmin) return;
			// If you are offline i don't need to refresh the token
			if (!onlineStatus) {
				logger.debugColor(
					'rgb(211, 15, 69)',
					`The app is offline so i will ignore the refresh logic`,
				);
				return;
			}
			// If any of these value is not set i can't work
			if (
				!accessToken ||
				!accessExpirationDate ||
				!refreshToken ||
				!refreshExpirationDate
			)
				return;

			const offsetAccessExpirationDate = durationOffset
				? accessExpirationDate.minus(durationOffset)
				: accessExpirationDate;

			const now = DateTime.now();

			// If now is larger than refresh expiration date clear the tokens
			if (now >= refreshExpirationDate) {
				logger.debug(
					`THE REDIRECT TOKEN EXPIRED AND I WILL KICK YOU OUT`,
				);
				clearTokens(redirect);
				return;
			}

			// If true the user access token is valid
			if (now < offsetAccessExpirationDate) {
				const diff = offsetAccessExpirationDate.diff(now);
				logger.debug(
					`The token is valid and is going to expire in `,
					msToTime(diff.toObject().milliseconds),
				);
				return;
			}

			triggerRefreshToken();
		},
		interval,
		[onlineStatus],
	);
}

interface TrackRefreshTokenArgs extends AuthEffectResponse {
	interval: number;
	isSupported: boolean;
	durationOffset?: DurationLike;
}

function parseJwt(token: string) {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function(c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join(''),
	);

	return JSON.parse(jsonPayload);
}
